import { Component, DoCheck, EventEmitter, Input, Output } from '@angular/core';
import { UpdatedSetupSummary } from '../../../../models/setup/setup.models';
import { State } from '@progress/kendo-data-query';
import { DailyOperationLogRecord } from '../../../../models/projects/operation.models';
import { AppData } from '../../../../models/app-data/app-data';
import { Subject } from 'rxjs/Subject';
import { NavController, NavParams } from 'ionic-angular';
import { CompleteDayService } from '../../../../services/complete-day/complete-day.service';
import { LocalSaveService } from '../../../../services/local/local-save.service';
import { LockedDayService } from '../../../../services/locked-day/locked-day.service';
import { OperationService } from '../../../../services/operations/operation/operation.service';
import { DateService } from '../../../../services/date/date.service';
import { SetupService } from '../../../../services/setup/setup.service';
import { DailyLogValidationService } from '../../../../services/daily-log/daily-log-validation/daily-log-validation.service';
import { DateParserService } from '../../../../services/daily-log/daily-log-time/date-parser-service';
import { DailyLogService } from '../../../../services/daily-log/daily-log.service';
import { DailyProgressReportService } from '../../../../services/daily-progress-report/daily-progress-report.service';
import { AppDataSyncService } from '../../../../services/app-data-sync/app-data-sync.service';
import { NavHelperService } from '../../../../services/nav/nav-helper.service';
import { catchError, debounce } from 'rxjs/operators';
import { PtLuxonDate } from '../../../../models/projects/date.models';
import { ErrorsPage } from '../errors-page/errors-page';
import { LockDayFilters } from '../../../../models/lock-day.models';
import { CompleteDayFilters } from '../../../../models/complete-day.models';
import { DailyProgressReportPreviewPage } from '../../../daily-progress-report/daily-progress-report-preview/daily-progress-report-preview';
import { DailyLogEntriesService } from '../../../../services/daily-log/daily-log-entries/daily-log-entries.service';
import { timer } from 'rxjs/internal/observable/timer';
import { ValidationResult } from '../../../../models/projects/validation.models';
import { TimeZoneService } from '../../../../services/time-zone/time-zone.service';
import { TimeZoneFilters } from '../../../../models/time-zones/time-zone.models';
import { DailyLogPermissions, LockedDayPermissions } from '../../../../models/permissions/permission.models';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { VesselService } from '../../../../services/vessel/vessel.service';
import { cloneDeep } from 'lodash';
import { ImplementerInitialiserPage } from '../../../implementer/implementer-initialiser/implementer-initialiser';
import { ImplementerService } from '../../../../services/implementer/implementer.service';
import { AppConfig } from '../../../../app/app.config';
import { DateTime } from 'luxon';
import { DailyLogSummaryHeaderStateService } from '../daily-log-summary-header/daily-log-summary-header-state.service';

@Component({
  selector: 'app-daily-log-date-picker',
  templateUrl: 'daily-log-date-picker.html'
})
export class DailyLogDatePickerComponent implements DoCheck {
  @Output() dateChange = new EventEmitter();
  @Output() syncComplete = new EventEmitter();
  @Output() loadingStarted = new EventEmitter();
  @Output() loadingFinished = new EventEmitter();
  @Output() markComplete = new EventEmitter();
  @Output() changesCancelled = new EventEmitter();
  @Output() changesSaved = new EventEmitter();
  @Output() syncingStarted = new EventEmitter();
  @Output() syncingFinished = new EventEmitter();
  @Output() customButtonPressed = new EventEmitter();
  @Output() customButton2Pressed = new EventEmitter();
  @Output() customButton3Pressed = new EventEmitter();

  // Emits when a completed day is reopened, including recent-day editing.
  @Output() dayUnlockedForEditing = new EventEmitter();
  @Output() dateBeforeInitialSetup: EventEmitter<boolean> = new EventEmitter();

  @Input() confirmedChange: Subject<AppData> = new Subject<AppData>();
  @Input() validationResult: ValidationResult;
  @Input() parentComponentCompleted: boolean;
  @Input() contentLoading: boolean = false;
  @Input() initialLoadComplete: boolean = false;
  @Input() canSaveDay: boolean;
  @Input() canMarkComplete: boolean;
  @Input() validationLoading: boolean;
  displayedValidationResult: ValidationResult;
  @Input() hidePreviewDpr: boolean;
  @Input() disableLockDayFunctionality: boolean;

  @Input() customButtonText: string;
  @Input() customButtonDisabled: boolean;
  @Input() customButtonDisplay: boolean;
  @Input() customButton2Text: string;
  @Input() customButton2Disabled: boolean;
  @Input() customButton2Display: boolean;
  @Input() customButton3Text: string;
  @Input() customButton3Disabled: boolean;
  @Input() customButton3Display: boolean;

  config = AppConfig.settings;
  dailyLogDate: PtLuxonDate = new PtLuxonDate();
  latestDate: DateTime;
  activeDayNavigationLoading = false;
  public mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];

  currentDate: PtLuxonDate = new PtLuxonDate();
  dailyLogDisplayDate: string;

  daysInPast: number = 0;

  canGoForward: boolean = false;

  dayInMilliseconds: number = 86400000;
  weekInMilliseconds: number = 691200000;

  summaryLoading: boolean;
  dateLoading: boolean = true;
  setupSummary: UpdatedSetupSummary;
  lockedDayLoading: boolean;

  state: State;

  setupComplete: boolean = false;
  isImplementerActiveForDay: boolean; // when true the day will be open for editing regardless of dayLockedByDate
  dayCompleted: boolean;
  dayLockedByDate: boolean = false;
  lockable: boolean = false;
  savingChanges: boolean;
  editDayLoading: boolean;
  canEditRecentDay: boolean = false;
  dayWorkflowError: string;

  dailyLogData: DailyOperationLogRecord[] = [];

  sidebarCollapsed: boolean;
  loaded: boolean = false;

  dataToUpdate: AppData = new AppData();

  // Leave the selection unset until the reporting day loads; calendar today can
  // briefly make the header show an incorrect Active day shortcut.
  currentLogDate: DateTime;
  maxDate: DateTime = DateTime.local();
  dateChangeLoading: boolean;
  private dateNavigationVersion = 0;
  debounceActive = false;

  permissions: DailyLogPermissions = new DailyLogPermissions();
  lockedDayPermissions: LockedDayPermissions = new LockedDayPermissions();

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public completeDayService: CompleteDayService,
    private localSaveService: LocalSaveService,
    public lockedDayService: LockedDayService,
    public operationService: OperationService,
    public dateService: DateService,
    public setupService: SetupService,
    public dailyLogValidatorService: DailyLogValidationService,
    public dateParserService: DateParserService,
    public dailyLogService: DailyLogService,
    private dprService: DailyProgressReportService,
    private appDataSyncService: AppDataSyncService,
    private navHelperService: NavHelperService,
    private dailyLogEntriesService: DailyLogEntriesService,
    private timeZoneService: TimeZoneService,
    private permissionService: PermissionsService,
    private vesselService: VesselService,
    private implementerService: ImplementerService,
    private headerState: DailyLogSummaryHeaderStateService,
  ) {
    this.dateLoading = true;
  }

  get validationPending(): boolean {
    return this.contentLoading || this.dateLoading || this.dateChangeLoading
      || this.validationLoading || this.editDayLoading || !this.initialLoadComplete;
  }

  ngDoCheck() {
    // A new section page mounts before its API requests finish. Keep the last
    // verified count for this reporting day while its own validation loads.
    if (!this.displayedValidationResult && this.validationPending) {
      this.displayedValidationResult = this.headerState?.getValidationForCurrentDay();
    }
    // Pages may clear or mutate their working result while validating. Like
    // the daily log, publish the replacement only when validation has settled.
    if (!this.validationPending && this.validationResult) {
      this.displayedValidationResult = cloneDeep(this.validationResult);
    }
  }

  async ngOnInit() {
    this.summaryLoading = true;
    this.dateLoading = true;
    this.loadingStarted.emit();

    this.permissions = await this.permissionService.dailyLogPermissions();

    this.confirmedChange
      .pipe(
        catchError(err => {
          this.debounceActive = false;
          throw (err.error);
        }),
        debounce(() => timer(600)))
      .subscribe(val => {
        this.appDataSyncService.syncLocalData(val)
          .then(res => {
            this.debounceActive = false;
            this.syncComplete.emit();
          });
        this.dataToUpdate = new AppData();
      });

    this.ionViewEnter();
  }

  async ionViewEnter() {
    this.loaded = false;
    this.dateLoading = true;
    this.loadingStarted.emit();

    this.latestDate = await this.completeDayService.getLatestDate() as DateTime;

    this.maxDate = cloneDeep(this.latestDate);

    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();

    if (this.dailyLogDate.dateTime != null) {
      this.currentLogDate = cloneDeep(this.dailyLogDate.dateTime);
    }

    await this.updateSetupSummary();

    this.setDisplayDate();
    this.state = this.dailyLogService.getState();

    await this.checkImplementerActive();
    await this.checkDailyLogMovement();
    await this.checkDayLocked();
    await this.checkDayCompleted();

    this.daysInPast = await this.currentDate.getDay() - await this.dailyLogDate.getDay();
  }

  async updateSetupSummary() {
    const summary = await this.setupService.getSetupSummary(this.dailyLogDate, true);

    this.setupService.saveSetup(summary);
    const setupNeeded = summary.phaseId == null ||
      summary.projectId == null ||
      summary.stageId == null;

    if (setupNeeded) {
      this.setupComplete = false;
      this.dateBeforeInitialSetup.emit(true);
    } else {
      this.setupComplete = true;
      this.dateBeforeInitialSetup.emit(false);
    }

    this.vesselService.getEditor().then(value => {
      this.navHelperService.updateSetupConfigured(!setupNeeded);
      this.setupSummary = summary;
      this.setupSummary.vesselName = value.name;
      this.summaryLoading = false;
    });
  }

  // if the user changes system clock to go backwards, (i.e GMT +1 to UTC 0), then when the user clicks on the datepicker to change dates
  // the max date will be set to the current log date -1 day, so this will be called when the user hovers over the datepicker to update these values
  async updateDailyLogDate() {
    if (this.dateChangeLoading) { return; }
    const version = this.dateNavigationVersion;
    // Hover is a read-only refresh. A delayed result must neither persist the
    // old day nor overwrite a selection made while this request was pending.
    const day = await this.dailyLogService.getCurrentLogDay(false);
    if (this.dateChangeLoading || version !== this.dateNavigationVersion) { return; }
    this.dailyLogDate = day;

    if (this.dailyLogDate != null) {
      this.dailyLogDate = cloneDeep(this.dailyLogDate);
      this.currentDate = cloneDeep(this.dailyLogDate);
      this.currentLogDate = cloneDeep(this.dailyLogDate.dateTime);

      if (this.maxDate < this.dailyLogDate.dateTime) {
        this.maxDate = cloneDeep(this.dailyLogDate.dateTime);
      }
    }
  }

  async setDate(value: DateTime) {
    this.dateNavigationVersion++;
    // if user has changed machine timezone, then we need to update this or the wrong daily log date will get saved, and wrong data will show
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();

    this.dateChangeLoading = true;
    this.dateLoading = true;
    this.summaryLoading = true;
    this.loadingStarted.emit();
    this.loaded = false;
    this.initialLoadComplete = false;

    this.dailyLogDate.dateTime = value;

    const tz = await this.timeZoneService.getEditorLocally({dateTime: value});

    if (tz) {
      this.dailyLogDate.setOffset(tz.secondsOffsetFromUtc);
    } else {
      this.dailyLogDate.setOffset(0);
    }

    this.currentLogDate = cloneDeep(this.dailyLogDate.dateTime); // used for the datepicker in the daily log datepicker

    await this.checkDayLocked();
    await this.checkDayCompleted();
    await this.checkImplementerActive();
    await this.updateSetupSummary();
    await this.checkDailyLogMovement();

    this.dailyLogService.setCurrentLogDay(this.dailyLogDate.dateTime);
    this.dateChange.emit();
    this.daysInPast = await this.currentDate.getDay() - await this.dailyLogDate.getDay();

    this.setDisplayDate();
  }

  async decreaseDate() {
    // update this in case user has changed machine time
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();

    // any offset changes between the two dates are applied in setDate()
    const newLocalDate = this.dailyLogDate.dateTime.minus({day: 1});
    await this.setDate(newLocalDate);
  }

  async increaseDate() {
    // if user has changed machine timezone, then we need to update this or the wrong daily log date will get saved, and wrong data will show
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();

    // any offset changes between the two dates are applied in setDate()
    const newLocalDate = this.dailyLogDate.dateTime.plus({day: 1});
    await this.setDate(newLocalDate);
  }

  async completeDay() {
    const instances = await this.operationService.getOperationsFromLogs(this.dailyLogData);

    this.navCtrl.push(ErrorsPage,
      {
        validationResult: this.validationResult,
        overlapTimes: this.dailyLogValidatorService.findOverlapTimes(instances, this.dailyLogDate),
        instances: instances,
        showActionButtons: !this.dayCompleted && (!this.config.isImplementerEnvironment || this.config.isImplementerEnvironment && this.isImplementerActiveForDay),
        completeDay: true,
        date: this.dailyLogDate,
        refresh: true
      });
  }

  async removeCompleteDay() {
    this.editDayLoading = true;
    const completeDayEditor = await this.completeDayService.setDayNotComplete(this.dailyLogDate);
    if (completeDayEditor) {
      this.dayCompleted = completeDayEditor.isComplete;
      this.dayUnlockedForEditing.emit();
    }

    this.editDayLoading = false;
  }

  async editRecentDay() {
    if (this.editDayLoading || this.validationPending || !this.setupComplete
      || !this.permissions.canAmendPreviousDay || !this.canEditRecentDay || this.config.isImplementerEnvironment) {
      return;
    }
    this.editDayLoading = true;
    this.dayWorkflowError = null;
    const date = cloneDeep(this.dailyLogDate);
    try {
      await this.completeDayService.editRecentDay(date);
      this.navHelperService.updateDailyLogRequiresReload(true);
      if (date.dateTime.toMillis() !== this.dailyLogDate.dateTime.toMillis()) { return; }
      await this.checkDayCompleted();
      await this.checkDayLocked();
      this.navHelperService.updateCanEnterData(!this.dayCompleted && this.dayLockedByDate);
      this.dayUnlockedForEditing.emit();
    } catch {
      this.dayWorkflowError = 'Unable to edit this day. Refresh and check that it is still the current or previous day.';
    } finally {
      this.editDayLoading = false;
    }
  }

  async checkDayLocked() {
    this.canEditRecentDay = false;
    this.dayWorkflowError = null;
    const filters = new LockDayFilters();

    filters.date = cloneDeep(this.dailyLogDate.dateTime);

    let lockedSummary = await this.lockedDayService.checkDay(filters);

    this.dayLockedByDate = lockedSummary.lockedByDate;
    this.lockable = lockedSummary.lockable;
    this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.dailyLogDate);
    if (!this.config.isImplementerEnvironment) {
      const workflow = await this.completeDayService.getWorkflow(this.dailyLogDate);
      this.canEditRecentDay = workflow.canEdit;
    }
    const timeZoneFilters = new TimeZoneFilters();
    timeZoneFilters.dateTime = lockedSummary.latestDate;

    await this.checkDailyLogMovement();

    this.lockedDayLoading = false;
  }

  async checkDayCompleted() {
    const filters = new CompleteDayFilters();
    filters.date = cloneDeep(this.dailyLogDate.dateTime);
    this.dayCompleted = await this.completeDayService.checkDay(filters);
  }

  // isImplementerEnvironment variable only tells us if the environment is the implementer, we still need to check if current date falls between
  // snapshotSummary start and end dates, if it does then we know this date is editable
  async checkImplementerActive() {
    this.isImplementerActiveForDay = await this.implementerService.implementerIsActive(this.config.isVesselEnvironment, cloneDeep(this.dailyLogDate));
  }

  openImplementerInitialiser() {
    if (!this.lockedDayPermissions?.canAccessLockedDay) { return; }
    this.navCtrl.push(ImplementerInitialiserPage, {
      dailyLogDate: this.dailyLogDate,
      lockedDayPermissions: this.lockedDayPermissions
    });
  }

  openDailyProgressReportPreview() {
    this.navCtrl.push(DailyProgressReportPreviewPage, {date: this.dailyLogDate});
  }

  async openErrorModal() {
    if (this.validationPending) { return; }
    const operationIds = this.dailyLogData
      .filter(p => !!p.operationInstanceId)
      .map(p => p.operationInstanceId)
      .filter((id, index, array) => array.indexOf(id) === index);

    const instances = await this.operationService
      .getProjectSpecificOperationInstances(operationIds);

    this.navCtrl.push(ErrorsPage,
      {
        date: this.dailyLogDate,
        validationResult: this.validationResult,
        overlapTimes: this.dailyLogValidatorService.findOverlapTimes(instances, this.dailyLogDate),
        instances: instances,
        showActionButtons: !this.dayCompleted && (!this.config.isImplementerEnvironment || this.config.isImplementerEnvironment && this.isImplementerActiveForDay),
        completeDay: false,
        refresh: true
      });
  }

  async checkDailyLogMovement() {
    const timeZone = await this.timeZoneService.getEditorLocally({
      dateTime: this.dailyLogDate.dateTime,
      dateAmbiguous: false
    });

    if (timeZone) {
      if (this.dayCompleted) {
        this.canGoForward = true;
      } else if (this.latestDate > this.dailyLogDate.dateTime) {
        this.canGoForward = true;
      } else {
        this.canGoForward = false;
      }
    } else {
      this.canGoForward = true;
    }
  }

  markFormComplete() {
    this.markComplete.emit();
  }

  emitCustomButtonPress() {
    this.customButtonPressed.next(true);
  }

  emitCustomButton2Press() {
    this.customButton2Pressed.next(true);
  }

  emitCustomButton3Press() {
    this.customButton3Pressed.next(true);
  }

  private async setDisplayDate() {
    this.dailyLogDisplayDate = this.dailyLogDate.dateTime.toFormat('EEEE, dd MMMM y');
    this.dateLoading = false;
    this.dateChangeLoading = false;
    this.loadingFinished.emit();
    this.initialLoadComplete = true;
  }

  async goToActiveLogDay() {
    const date = this.latestDate || this.maxDate;
    if (this.activeDayNavigationLoading || this.contentLoading || this.dateLoading || this.dateChangeLoading
      || !date?.isValid || date.toMillis() === this.dailyLogDate?.dateTime.toMillis()) { return; }
    this.activeDayNavigationLoading = true;
    try { await this.setDate(date); }
    finally { this.activeDayNavigationLoading = false; }
  }
}



// WEBPACK FOOTER //
// ./src/pages/shared/components/daily-log-date-picker/daily-log-date-picker.ts